import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  Paper,
  IconButton,
  Box,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import Navbar from "../../../../globals/components/Navbar";
import { apiClient } from "../../../../api/httpClient";
import { toast } from "react-toastify";
import TablePaginationActions from "../../../../globals/components/TablePaginationActions";

function BondTypeList() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedBondType, setSelectedBondType] = useState(null);
  const [newBondType, setNewBondType] = useState("");
  const [bondTypes, setBondTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Fetch all bond types on component mount
  useEffect(() => {
    const fetchBondTypes = async () => {
      setLoading(true);
      try {
        const response = await apiClient("/bondtypes/", "GET");
        setBondTypes(response || []);
      } catch (error) {
        toast.error("Failed to load bond types.");
        console.error("Error fetching bond types:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBondTypes();
  }, []);

  // Open dialog to add or edit bond type
  const handleOpenDialog = (bondType = null) => {
    if (bondType) {
      setEditMode(true);
      setSelectedBondType(bondType);
      setNewBondType(bondType.type);
    } else {
      setEditMode(false);
      setNewBondType("");
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedBondType(null);
  };

  // Handle save (add or edit) bond type
  const handleSaveBondType = async () => {
    setSubmitting(true);
    try {
      for (let i = 0; i < bondTypes.length; i++) {
        if (bondTypes[i].type.toLowerCase() === newBondType.toLowerCase()) {
          toast.error("Bond type already exists.");
          return;
        }
      }
      if (editMode) {
        if (!selectedBondType?.bond_type_id) return;
        await apiClient(`/bondtypes/${selectedBondType.bond_type_id}/`, "PATCH", { type: newBondType });
        toast.success("Bond type updated successfully.");
      } else {
        await apiClient("/bondtypes/", "POST", { type: newBondType });
        toast.success("Bond type added successfully.");
      }
      handleCloseDialog();
      // Refresh bond types after save
      const response = await apiClient("/bondtypes/", "GET");
      setBondTypes(response || []);
    } catch (error) {
      toast.error("Failed to save bond type.");
      console.error("Error saving bond type:", error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete bond type
  const handleDeleteBondType = async (bondTypeId) => {
    if (!window.confirm("Are you sure you want to delete this bond type?")) return;

    setSubmitting(true);
    try {
      await apiClient(`/bondtypes/${bondTypeId}/`, "DELETE");
      toast.success("Bond type deleted successfully.");
      // Refresh bond types after delete
      const response = await apiClient("/bondtypes/", "GET");
      setBondTypes(response || []);
    } catch (error) {
      toast.error("Failed to delete bond type.");
      console.error("Error deleting bond type:", error);
    } finally {
      setSubmitting(false);
    }
  };

  // Pagination controls
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filtered and paginated bond types for display
  const filteredBondTypes = bondTypes.filter((bondType) =>
    bondType.type.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const currentRows = filteredBondTypes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <Navbar />

      <Box sx={{ maxWidth: "100%", padding: "20px" }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
          Bond Types
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}>
          <TextField
            label="Search by Bond Type"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              width: "300px",
              borderRadius: "8px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
            slotProps={{ input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}}
          />
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            New Bond Type
          </Button>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "200px" }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ width: "100%", overflowX: "auto", mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow style={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell>Bond Type</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentRows.map((bondType) => (
                  <TableRow key={bondType.bond_type_id}>
                    <TableCell>{bondType.type}</TableCell>
                    <TableCell>
                      <IconButton color="primary" onClick={() => handleOpenDialog(bondType)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteBondType(bondType.bond_type_id)}>
  <DeleteIcon style={{ color: 'red' }} />
</IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          ActionsComponent={TablePaginationActions}
          component="div"
          count={filteredBondTypes.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{editMode ? "Edit Bond Type" : "Add New Bond Type"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Bond Type"
            type="text"
            fullWidth
            variant="outlined"
            value={newBondType}
            onChange={(e) => setNewBondType(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSaveBondType} color="primary" disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : editMode ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default BondTypeList;
